import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';
import type { ProblemDetailsBody } from '@core/http/problem-details.model';
import { HttpFailureLogService } from './http-failure-log.service';
import { redactUrl } from './telemetry-redaction.util';

export const CORRELATION_ID_HEADER = 'X-Correlation-Id';

const MAX_PROBLEM_TEXT_LENGTH = 300;

/**
 * Records every failed request into the diagnostics buffer. Must be registered FIRST in
 * `withInterceptors([...])` so the measured duration wraps every other interceptor.
 *
 * Never records request or response bodies, nor any header beyond the correlation id: those carry
 * clinical history and the bearer token respectively.
 */
export const telemetryInterceptor: HttpInterceptorFn = (request, next) => {
  const httpFailureLog = inject(HttpFailureLogService);
  const startedAt = performance.now();

  return next(request).pipe(
    tap({
      error: (error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) return;

        const problem = problemDetailsOf(error.error);

        // Recorded even when SUPPRESS_ERROR_TOAST is set: hiding a toast is a UX decision, not a
        // diagnostics one.
        httpFailureLog.record({
          atIso: new Date().toISOString(),
          method: request.method,
          path: redactUrl(request.urlWithParams),
          status: error.status,
          statusText: error.statusText,
          correlationId: error.headers.get(CORRELATION_ID_HEADER) ?? asText(problem?.correlationId),
          durationMs: Math.round(performance.now() - startedAt),
          problemTitle: cap(problem?.title),
          problemDetail: cap(problem?.detail),
        });
      },
    }),
  );
};

function problemDetailsOf(body: unknown): ProblemDetailsBody | null {
  return typeof body === 'object' && body !== null ? (body as ProblemDetailsBody) : null;
}

function asText(value: string | undefined): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function cap(value: string | undefined): string | null {
  const text = asText(value);
  if (!text) return null;

  return text.length > MAX_PROBLEM_TEXT_LENGTH ? `${text.slice(0, MAX_PROBLEM_TEXT_LENGTH - 1)}…` : text;
}
